import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchRaceBySlug } from '../../lib/queries';
import { trackEvent } from '../../lib/analytics';
import type { Race } from '../../lib/types';

interface Props {
  field: 'youtube_livestream_url' | 'spot_tracker_url';
  placeholderTitle: string;
  placeholderBody: string;
}

type State =
  | { kind: 'loading' }
  | { kind: 'redirecting' }
  | { kind: 'placeholder' }
  | { kind: 'race-not-found' }
  | { kind: 'error'; message: string };

export function RedirectToRaceUrlPage({ field, placeholderTitle, placeholderBody }: Props) {
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (!slug) {
      setState({ kind: 'race-not-found' });
      return;
    }

    let cancelled = false;
    fetchRaceBySlug(slug)
      .then((race: Race | null) => {
        if (cancelled) return;
        if (!race) {
          setState({ kind: 'race-not-found' });
          return;
        }
        const url = race[field];
        if (typeof url === 'string' && url.trim()) {
          // Count the click-through to the external destination for sponsor
          // reporting. Plausible sends events via sendBeacon, which survives the
          // redirect below; by this point (after the race fetch round-trip) the
          // async Plausible script has loaded, so the call isn't stuck in the
          // pre-load queue.
          const eventName = field === 'youtube_livestream_url' ? 'livestream_click' : 'map_click';
          trackEvent(eventName, { destination: url.trim() });
          setState({ kind: 'redirecting' });
          window.location.replace(url.trim());
        } else {
          setState({ kind: 'placeholder' });
        }
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setState({ kind: 'error', message: err.message });
      });

    return () => {
      cancelled = true;
    };
  }, [slug, field]);

  if (state.kind === 'loading' || state.kind === 'redirecting') {
    return (
      <div className="v2-tarawera" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--v2-text-secondary)' }}>Loading...</div>
      </div>
    );
  }

  if (state.kind === 'race-not-found') {
    return (
      <div className="v2-tarawera" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 400, padding: '0 24px' }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 8 }}>
            Race Not Found
          </h1>
          <p style={{ color: 'var(--v2-text-secondary)' }}>
            The race "{slug}" could not be found.
          </p>
        </div>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="v2-tarawera" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 400, padding: '0 24px' }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 8 }}>
            Something went wrong
          </h1>
          <p style={{ color: 'var(--v2-text-secondary)' }}>{state.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="v2-tarawera" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', maxWidth: 400, padding: '0 24px' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 16 }}>
          {placeholderTitle}
        </h1>
        <p style={{ color: 'var(--v2-text-secondary)' }}>{placeholderBody}</p>
      </div>
    </div>
  );
}
